/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */
import { css, html, LitElement } from 'lit-element';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';

/**
 * @prop {Number} value - Defines which stepper-item will be active. Takes any number between 1 and the total number of stepper-items.
 * @prop {Boolean} readonly - Default false. If set to `true`, the stepper will not be interactive.
 * @prop {Boolean} condensed - Default false. If set to `true`, the stepper will be shown in a smaller size, without label and will not be interactive.
 * @prop {String} color - Default neutral. The property allows to switch between the 'primary' and 'neutral' versions of the stepper.
 */
export class SHStepper extends LitElement {
  static get properties() {
    return {
      value: { type: String, reflect: true },
      condensed: { type: Boolean, reflect: true },
      readonly: { type: Boolean, reflect: true },
      color: { type: String, reflect: true },
    };
  }
  static get styles() {
    return [
      css`
        :host {
          width: 100%;
          height: auto;
          display: flex;
        }
        :host([readonly]) {
          pointer-events: none;
        }
      `,
    ];
  }
  render() {
    return html`<slot @slotchange=${this.slotChange} @click=${this.clickFromSlot}></slot>`;
  }

  constructor() {
    super();
    this.color = 'neutral';
  }

  connectedCallback() {
    super.connectedCallback();
    for (let i = 0; i < this.children.length; i++) {
      if (!this.children[i].disabled && !this.hasAttribute('tabindex')) {
        this.children[i].setAttribute('tabindex', '0');
      }
    }
  }

  clickFromSlot(e) {
    // only consider clics on stepper items
    if (e.target && e.target.tagName === 'SH-STEPPER-ITEM') {
      this.value = e.target.number;
      this._updateItems();
    }
  }

  // potentially there are several problem shere
  // 1. a user cannot control the numer property of an sh-tab item
  //    the system will will always trash it when the slot config changed
  //    this should be made clear to a user as part of the docs at least
  //    (i.e. "The number property will be controlled by the parent sh-stepper")
  slotChange() {
    // set number of each step based on its' index
    const items = this.querySelectorAll('sh-stepper-item');
    for (let i = 0; i < items.length; i++) {
      items[i].number = i + 1;

      if (items[i].hasAttribute('active')) {
        this.value = items[i].number;
      }
    }
    // update items on load
    this._updateItems();
    this._handleCondensedChange();
    this._handleColorChange();
  }

  updated(changedProperties) {
    if (changedProperties.has('condensed')) {
      this._handleCondensedChange();
    }
    if (changedProperties.has('value')) {
      this._updateItems();
    }
    if (changedProperties.has('color')) {
      this._handleColorChange();
    }
    const listOfProperties = ['value', 'condensed', 'readonly', 'color'];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }
  // update value and disabled state
  async _updateItems() {
    const items = this.querySelectorAll('sh-stepper-item');
    for (let i = 0; i < items.length; i++) {
      if (items[i].number > this.value) {
        items[i].active = false;
        await this.updateComplete;
        items[i].setAttribute('disabled', '');
      } else if (items[i].number === parseInt(this.value)) {
        items[i].active = true;
      } else {
        items[i].active = false;
        items[i].disabled = false;
      }
      // half-filled circles
      if (this.value > Math.floor(this.value) && i === Math.ceil(this.value - 1)) {
        items[i].setAttribute('half', '');
      } else {
        items[i].removeAttribute('half');
      }
    }
  }

  // condense items if wrapper is condensed
  _handleCondensedChange() {
    const items = this.querySelectorAll('sh-stepper-item');
    for (let i = 0; i < items.length; i++) {
      items[i].condensed = this.condensed;
    }
  }

  // update active items in terms of color if wrapper color property is changed to 'primary'
  _handleColorChange() {
    const items = this.querySelectorAll('sh-stepper-item');
    for (let i = 0; i < items.length; i++) {
      items[i].color = this.color;
    }
  }
}

customElements.define('sh-stepper', SHStepper);
